/**
 * Regole pure del codice via email nel passo 2FA: quale metodo mostrare per primo,
 * quando inviare il codice da solo e quanto aspettare prima di "Reinvia".
 */
export type TwoFactorMethod = 'totp' | 'email';

/** Secondi prima che "Reinvia" diventi disponibile: uguale al limite del server (twofa-send-email-code, edge e core: 60 s). */
export const RESEND_DELAY_SECONDS = 60;

/** L'app di autenticazione ha la precedenza; chi ha solo l'email parte dall'email. */
export function initialMethod(available: TwoFactorMethod[]): TwoFactorMethod {
  return available.includes('totp') ? 'totp' : 'email';
}

/** Invio automatico: una sola volta per passo, solo quando il metodo mostrato e' l'email. */
export function shouldAutoSendEmail(method: TwoFactorMethod, alreadyRequested: boolean): boolean {
  return method === 'email' && !alreadyRequested;
}

export interface SendEmailResponse {
  success?: boolean;
  retryAfter?: number;
  error?: string;
}

export interface SendEmailOutcome {
  /** Il codice e' stato inviato ora o era gia' stato inviato da poco (limite del server) */
  sent: boolean;
  /** Secondi di attesa prima di "Reinvia" */
  countdown: number;
  /** Messaggio da mostrare (null se non serve) */
  message: string | null;
  isError: boolean;
}

/**
 * Traduce la risposta di twofa-send-email-code. Se il server dice di aspettare (limite di 60 secondi
 * fra due invii) il codice precedente e' ancora valido: non e' un errore, si mostra il conto alla rovescia.
 */
export function interpretSendEmailResponse(data: SendEmailResponse | null | undefined): SendEmailOutcome {
  if (data?.success) {
    return { sent: true, countdown: RESEND_DELAY_SECONDS, message: null, isError: false };
  }
  if (data?.retryAfter && data.retryAfter > 0) {
    return {
      sent: true,
      countdown: Math.ceil(data.retryAfter),
      message: 'Ti abbiamo appena inviato un codice: controlla la posta.',
      isError: false,
    };
  }
  return {
    sent: false,
    countdown: 0,
    message: data?.error || "Errore nell'invio del codice. Riprova.",
    isError: true,
  };
}

// ─── Attivazione del codice via email (twofa-enable-email, action 'send') ───

export interface SetupSendResponse extends SendEmailResponse {
  /** Solo con l'attesa: l'ultimo codice inviato si puo' ancora usare (assente sulle edge vecchie). */
  codeStillValid?: boolean;
}

/**
 * - 'sent': codice appena inviato.
 * - 'recent_code': il server chiede di attendere e un codice mandato poco fa vale ancora (nessuna nuova email).
 * - 'wait': il server chiede di attendere e non c'e' un codice valido da usare (nessuna email partita).
 */
export type SetupSendStatus = 'sent' | 'recent_code' | 'wait';

export interface SetupSendOutcome {
  status: SetupSendStatus;
  countdown: number;
  isError: boolean;
  message: string | null;
}

/**
 * Risposta di twofa-enable-email a 'send'. Con l'attesa di 60 secondi NON e' partito nessun codice:
 * si dice "appena inviato" solo se il server lo conferma (success). Se la edge non dice nulla sul codice
 * precedente (versione vecchia) si sceglie la strada prudente: attesa, senza promettere un codice.
 */
export function interpretSetupSendResponse(data: SetupSendResponse | null | undefined): SetupSendOutcome {
  if (data?.success) return { status: 'sent', countdown: RESEND_DELAY_SECONDS, isError: false, message: null };
  if (data?.retryAfter && data.retryAfter > 0) {
    return {
      status: data.codeStillValid === true ? 'recent_code' : 'wait',
      countdown: Math.ceil(data.retryAfter),
      isError: false,
      message: null,
    };
  }
  return { status: 'wait', countdown: 0, isError: true, message: data?.error || "Errore nell'invio del codice. Riprova." };
}

/** Testo sotto le caselle / nell'introduzione, aggiornato col conto alla rovescia. */
export function setupSendNotice(status: SetupSendStatus | null, secondsLeft: number): string | null {
  if (status === 'recent_code') {
    return secondsLeft > 0
      ? `Ti abbiamo già mandato un codice da poco: usa quello o chiedine un altro tra ${secondsLeft}s.`
      : 'Ti abbiamo già mandato un codice da poco: usa quello o chiedine un altro.';
  }
  if (status === 'wait') {
    return secondsLeft > 0
      ? `Nessun nuovo codice inviato: hai chiesto un codice da poco. Potrai chiederne uno tra ${secondsLeft}s.`
      : null;
  }
  return null;
}
